import { useEffect, useRef } from 'react';
import { Caravan, ChevronDown, Ruler } from 'lucide-react';
import { useSearchStore } from '../../store/useSearchStore';
import { VEHICLE_KINDS, vehicleKindMeta } from '../../lib/fit';
import { formatFeet } from '../../lib/format';

/**
 * The highest-leverage control in the product: every unit on screen is graded
 * against these numbers, so it lives in the header and persists across visits.
 */
export default function VehicleProfile() {
  const vehicle = useSearchStore((s) => s.vehicle);
  const setVehicle = useSearchStore((s) => s.setVehicle);
  const setVehicleKind = useSearchStore((s) => s.setVehicleKind);
  const open = useSearchStore((s) => s.vehiclePanelOpen);
  const setOpen = useSearchStore((s) => s.setVehiclePanelOpen);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="flex items-center gap-2 rounded-full border border-slate-300 bg-white py-2 pl-3 pr-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400"
      >
        <Caravan className="h-4 w-4 text-brand-600" aria-hidden="true" />
        <span className="hidden sm:inline">{vehicleKindMeta(vehicle.kind).label}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
          {formatFeet(vehicle.length)}
        </span>
        <ChevronDown className={`h-4 w-4 text-brand-500 transition ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div role="dialog" aria-label="Your vehicle"
             className="absolute right-0 z-40 mt-2 w-[21rem] rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
          <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-3">
            <Ruler className="h-4 w-4 text-brand-600" aria-hidden="true" />
            <div>
              <p className="text-sm font-bold text-slate-900">Your vehicle</p>
              <p className="text-xs text-slate-500">Every unit is graded against these numbers.</p>
            </div>
          </div>

          <label className="field-label mb-1" htmlFor="vehicle-kind">Type</label>
          <select id="vehicle-kind" className="field-input mb-3" value={vehicle.kind}
                  onChange={(e) => setVehicleKind(e.target.value)}>
            {VEHICLE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>

          <div className="grid grid-cols-3 gap-3">
            <NumberField id="v-length" label="Length" value={vehicle.length} min={8} max={70} step={1}
                         onChange={(length) => setVehicle({ length })} />
            <NumberField id="v-width" label="Width" value={vehicle.width} min={5} max={14} step={0.5}
                         onChange={(width) => setVehicle({ width })} />
            <NumberField id="v-height" label="Height" value={vehicle.height} min={4} max={16} step={0.5}
                         onChange={(height) => setVehicle({ height })} />
          </div>

          <p className="mt-3 rounded-lg bg-slate-50 p-2.5 text-[12px] leading-snug text-slate-600">
            Units that don&apos;t fit stay visible, marked in red. A bay that misses by two feet is
            still worth knowing about before you drive over.
          </p>
        </div>
      )}
    </div>
  );
}

function NumberField({ id, label, value, min, max, step, onChange }) {
  return (
    <div>
      <label className="field-label mb-1" htmlFor={id}>{label} (ft)</label>
      <input id={id} type="number" className="field-input" value={value ?? ''}
             min={min} max={max} step={step}
             onChange={(e) => {
               const next = e.target.value === '' ? null : Number(e.target.value);
               if (next === null || (next >= min && next <= max)) onChange(next);
             }} />
    </div>
  );
}
